import { formatARS, formatProductName } from '../lib/formatters';
import { getItemPrice, getItemQuantity, getLineSubtotal } from './cartPrice';

// WhatsApp contact number for all storefront deep links.
// CRA inlines REACT_APP_* at build time, so production should still set
// REACT_APP_WHATSAPP_NUMBER in Railway. The fallback keeps wa.me links from
// silently becoming "https://wa.me/?text=..." when the env var is missing.
const DEFAULT_WHATSAPP_NUMBER = '5491124001228';

export function normalizeWhatsAppNumber(value) {
  let digits = String(value || '').replace(/\D+/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  digits = digits.replace(/^0+/, '');

  if (!digits) return DEFAULT_WHATSAPP_NUMBER;

  // Argentina mobile links should include country code 54 + mobile marker 9.
  // Accept common admin inputs like "+54 11 2400-1228" or "11 2400-1228"
  // and turn them into the wa.me-safe form "5491124001228".
  if (digits.startsWith('54') && !digits.startsWith('549') && digits.length >= 12) {
    return `549${digits.slice(2)}`;
  }
  if (!digits.startsWith('54') && digits.length >= 10 && digits.length <= 11) {
    return `549${digits}`;
  }

  return digits;
}

export const PINKSISLY_WHATSAPP_NUMBER = normalizeWhatsAppNumber(
  process.env.REACT_APP_WHATSAPP_NUMBER
);

if (!process.env.REACT_APP_WHATSAPP_NUMBER && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    `[whatsappOrder] REACT_APP_WHATSAPP_NUMBER is not set — using fallback ${DEFAULT_WHATSAPP_NUMBER}.`
  );
}

const siteOrigin = 'https://www.pinksislymoda.com';

function getRuntimeWhatsAppNumber() {
  if (typeof window !== 'undefined') {
    const runtimeNumber = window.__PINKSISLY_STORE_SETTINGS__?.['whatsapp.number'];
    if (runtimeNumber) return runtimeNumber;
  }
  return PINKSISLY_WHATSAPP_NUMBER;
}

function buildWaUrl(message, number) {
  return `https://wa.me/${normalizeWhatsAppNumber(number || getRuntimeWhatsAppNumber())}?text=${encodeURIComponent(message)}`;
}

export function buildWhatsAppUrl(
  message = 'Hola Pinksisly, me gustaría hacer una consulta.',
  number
) {
  return buildWaUrl(message, number);
}

export function buildProductWhatsAppUrl({ product, selectedColor, selectedSize, quantity = 1 }) {
  const name = formatProductName(product?.name || product?.title || 'Producto');
  const url = product?.slug ? `${siteOrigin}/product/${product.slug}` : siteOrigin;
  const parts = [
    'Hola Pinksisly, quiero consultar por este producto:',
    name,
    selectedColor ? `Color: ${selectedColor}` : null,
    selectedSize ? `Talle: ${selectedSize}` : null,
    quantity ? `Cantidad: ${quantity}` : null,
    product?.price ? `Precio: ${formatARS(product.price)}` : null,
    `Link: ${url}`,
  ].filter(Boolean);
  return buildWaUrl(parts.join('\n'));
}

export function buildCartWhatsAppUrl(cartItems = []) {
  const lines = cartItems.map((item, index) => {
    const name = formatProductName(item.productName || item.name || `Producto ${index + 1}`);
    const color = item.variant?.color || item.color || item.colorName;
    const size = item.variant?.size || item.size || item.sizeName;
    const qty = getItemQuantity(item);
    const unit = getItemPrice(item);
    const subtotal = getLineSubtotal(item);
    return [
      `${index + 1}. ${name}`,
      color ? `Color: ${color}` : null,
      size ? `Talle: ${size}` : null,
      `Cantidad: ${qty}`,
      unit ? `Precio unitario: ${formatARS(unit)}` : null,
      subtotal ? `Subtotal: ${formatARS(subtotal)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  });
  const total = cartItems.reduce((sum, item) => sum + getLineSubtotal(item), 0);
  const message = [
    'Hola Pinksisly, quiero hacer este pedido:',
    ...lines,
    total ? `Total: ${formatARS(total)}` : null,
    'Quiero coordinar pago y envío.',
  ]
    .filter(Boolean)
    .join('\n');
  return buildWaUrl(message);
}
